import { css, SerializedStyles } from '@emotion/core';
import {
  Theme,
  normalText,
  secondaryText,
  titleText,
  helpText,
} from 'features/Theme';

const styles = {
  bodyContainer: (theme: Theme): SerializedStyles => css`
    ${secondaryText(theme)};
  `,

  pageTitleText: (theme: Theme): SerializedStyles => css`
    ${titleText(theme)}
    font-weight: 600;
    text-align: center;
  `,

  subtitleText: (theme: Theme): SerializedStyles => css`
    ${normalText(theme)};
    text-align: center;
  `,

  helpOutlineIcon: (theme: Theme): SerializedStyles => css`
    vertical-align: bottom;
    color: ${theme.mui.palette.primary.main};
    .MuiSvgIcon-root {
      vertical-align: bottom;
      font-size: 18px;
      padding: 12.5px;
      margin: -10px;
    }
  `,

  enquiryIcon: (theme: Theme): SerializedStyles => css`
    position: absolute;
    right: ${theme.mui.spacing(2)}px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 100;
    vertical-align: bottom;
    color: ${theme.mui.palette.primary.main};
    .MuiSvgIcon-root {
      vertical-align: bottom;
      font-size: 16px;
      padding: 15px;
      margin: -10px;
    }
  `,

  /** TODO a lot of these styles should become obsolete when we have
   * the proper content placement in order for the dropdowns **/

  panelText: (theme: Theme): SerializedStyles => css`
    ${secondaryText(theme)};
    display: inline-block;
    width: 100%;
  `,

  panelIcon: (theme: Theme): SerializedStyles => css`
    padding-right: ${theme.mui.spacing(1)}px;
    float: left;
  `,

  panelTitle: (theme: Theme): SerializedStyles => css`
    ${secondaryText(theme)};
    font-weight: ${theme.mui.typography.fontWeightBold};
    display: inline;
    float: left;
  `,

  panelContentTitle: (theme: Theme): SerializedStyles => css`
    ${secondaryText(theme)};
    display: inline;
    float: left;
  `,

  panelTotalTitle: (theme: Theme): SerializedStyles => css`
    ${secondaryText(theme)};
    font-weight: ${theme.mui.typography.fontWeightBold};
    display: inline;
    float: left;
  `,

  panelTotalText: (theme: Theme): SerializedStyles => css`
    ${secondaryText(theme)};
    font-weight: ${theme.mui.typography.fontWeightBold};
    display: inline;
    float: right;
  `,

  panelSecondaryText: (theme: Theme): SerializedStyles => css`
    ${secondaryText(theme)};
    max-width: 33%;
    display: inline;
    float: right;
    block-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-all;
  `,

  panelFinePrint: (theme: Theme): SerializedStyles => css`
    ${helpText(theme)};
    margin-top: ${theme.mui.spacing(1)}px;
  `,

  consentField: (theme: Theme): SerializedStyles => css`
    .MuiFormControlLabel-root {
      align-items: flex-start;
    }
    .MuiCheckbox-root {
      margin-left: ${theme.mui.spacing(1) / 2}px;
      padding-top: 0;
    }
  `,

  checkboxLabel: (theme: Theme): SerializedStyles => css`
    ${helpText(theme)}
  `,

  shadedRoundedContainer: (theme: Theme): SerializedStyles => css`
    ${secondaryText(theme)};
    background-color: #f2f7f8;
    padding: ${theme.mui.spacing(2)}px;
    border-radius: ${theme.shape.borderRadiusLarge}px;
    display: flex;
    flex-wrap: wrap;
  `,

  hostedFieldsTitle: (theme: Theme): SerializedStyles => css`
    ${secondaryText(theme)};
    font-weight: ${theme.mui.typography.fontWeightBold};
    margin-bottom: ${theme.mui.spacing(2)}px;
    flex: 0 0 100%;
  `,

  hostedFieldsSubtitle: (theme: Theme): SerializedStyles => css`
    ${secondaryText(theme)};
    margin-bottom: ${theme.mui.spacing(2)}px;
    color: ${theme.mui.palette.text.secondary};
    flex: 0 0 100%;
  `,

  hostedFieldsFooter: (theme: Theme): SerializedStyles => css`
    ${secondaryText(theme)};
    color: ${theme.mui.palette.text.secondary};
    flex: 0 0 100%;
  `,

  hostedFieldSection: (theme: Theme): SerializedStyles => css`
    position: relative;
  `,

  hostedFieldLabel: (theme: Theme) => css`
    display: block;
    transform: translate(0, 1.5px) scale(0.75); // Mui hardcode
    transform-origin: top left;
    top: ${theme.mui.spacing(1)}px;
    left: 0;
    position: absolute;
    color: ${theme.mui.palette.text.secondary};
    ${theme.mui.typography.body1};
    line-height: 1;
    padding-left: ${theme.mui.spacing(1)}px;
  `,
  hostedField: (theme: Theme): SerializedStyles => {
    const underline = 'rgba(0, 0, 0, 0.42)'; // Mui hardcode this

    return css`
      height: 1.1875em; // Mui hardcode

      padding: ${theme.mui.spacing(1)}px;
      padding-top: ${theme.mui.spacing(3)}px;

      &:after {
        border-bottom: 2px solid ${theme.mui.palette.primary.main};
        left: 0;
        bottom: 0;
        content: '';
        position: absolute;
        right: 0;
        transform: scaleX(0);
        transition: ${theme.mui.transitions.create('transform', {
          duration: theme.mui.transitions.duration.shorter,
          easing: theme.mui.transitions.easing.easeOut,
        })};
        pointer-events: none;
      }

      &.braintree-hosted-fields-focused:after {
        transform: scaleX(1);
      }

      &.braintree-hosted-fields-invalid:after {
        border-bottom-color: ${theme.mui.palette.error.main};
        transform: scaleX(1);
      }

      &:before {
        border-bottom: 1px solid ${underline};
        left: 0;
        bottom: 0;
        content: '\\00a0';
        position: absolute;
        right: 0;
        pointer-events: none;
        transition: ${theme.mui.transitions.create('border-bottom-color', {
          duration: theme.mui.transitions.duration.shorter,
        })};
      }
      &:hover:before {
        border-bottom: 2px solid ${theme.mui.palette.text.primary};
        @media (hover: none) {
          border-bottom: 1px solid ${underline};
        }
      }

      &.braintree-hosted-fields-invalid:before {
        border-bottom-color: ${theme.mui.palette.error.main};
      }
    `;
  },

  cardImages: (theme: Theme): SerializedStyles => css`
    padding-left: ${theme.mui.spacing(1)}px;
  `,

  cardImage: (theme: Theme): SerializedStyles => css`
    margin-right: ${theme.mui.spacing(1) / 2}px;
    margin-top: ${theme.mui.spacing(1)}px;
    width: ${theme.mui.spacing(3)}px;
  `,
};

export default styles;
